import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CustomerAccountOperationComponent } from './customer-account-operation.component';
import {
  CustomerAccountOrganization,
  CustomerAccountReportRow,
  CustomerAccountService
} from './customer-account.service';
import { accountError } from './customer-account.utils';

@Component({
  selector: 'app-customer-account-report',
  templateUrl: './customer-account-report.component.html',
  styleUrls: ['./customer-account.scss']
})
export class CustomerAccountReportComponent implements OnInit, OnDestroy {
  organizations: CustomerAccountOrganization[] = [];
  rows: CustomerAccountReportRow[] = [];
  orgId = 0;
  search = '';
  status = '';
  sort = 'client_name';
  direction: 'asc' | 'desc' = 'asc';
  total = 0;
  loading = false;
  error = '';
  private revision = 0;
  private applied = {
    org_id: 0,
    search: '',
    status: '',
    sort: 'client_name',
    direction: 'asc' as 'asc' | 'desc'
  };
  private subscription = new Subscription();
  constructor(
    private accounts: CustomerAccountService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog
  ) {}
  async ngOnInit() {
    try {
      this.organizations = (await firstValueFrom(this.accounts.organizations())).data || [];
    } catch (error) {
      this.error = accountError(error, 'Не удалось загрузить организации');
      return;
    }
    this.subscription.add(
      this.route.queryParamMap.subscribe((params) => {
        this.orgId = Number(params.get('org_id')) || this.organizations[0]?.id || 0;
        this.search = params.get('search') || '';
        this.status = params.get('status') || '';
        this.sort = params.get('sort') || 'client_name';
        this.direction = params.get('direction') === 'desc' ? 'desc' : 'asc';
        this.applied = {
          org_id: this.orgId,
          search: this.search,
          status: this.status,
          sort: this.sort,
          direction: this.direction
        };
        void this.reload();
      })
    );
    this.subscription.add(this.accounts.changed.subscribe(() => void this.reload()));
  }
  ngOnDestroy() {
    this.revision++;
    this.subscription.unsubscribe();
  }
  apply() {
    if (
      this.applied.org_id === this.orgId &&
      this.applied.search === this.search.trim() &&
      this.applied.status === this.status &&
      this.applied.sort === this.sort &&
      this.applied.direction === this.direction
    ) {
      void this.reload();
      return;
    }
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        org_id: this.orgId || null,
        search: this.search.trim() || null,
        status: this.status || null,
        sort: this.sort === 'client_name' ? null : this.sort,
        direction: this.direction === 'asc' ? null : this.direction
      }
    });
  }
  async reload() {
    this.revision++;
    this.rows = [];
    this.total = 0;
    this.loading = false;
    await this.loadMore();
  }
  async loadMore() {
    if (this.loading || !this.applied.org_id) return;
    const revision = this.revision;
    this.loading = true;
    this.error = '';
    try {
      const response = await firstValueFrom(
        this.accounts.report({
          org_id: this.applied.org_id,
          search: this.applied.search || undefined,
          status: this.applied.status || undefined,
          sort: this.applied.sort,
          direction: this.applied.direction,
          offset: this.rows.length,
          limit: 30
        })
      );
      if (revision !== this.revision) return;
      const known = new Set(this.rows.map((row) => row.client_id));
      this.rows = [
        ...this.rows,
        ...(response.data || []).filter((row) => !known.has(row.client_id))
      ];
      this.total = Number(response.length || 0);
    } catch (error) {
      if (revision === this.revision)
        this.error = accountError(error, 'Не удалось загрузить взаиморасчёты');
    } finally {
      if (revision === this.revision) this.loading = false;
    }
  }
  edge(event: Event) {
    const element = event.target as HTMLElement;
    if (
      !this.loading &&
      !this.error &&
      this.rows.length < this.total &&
      element.scrollHeight - element.scrollTop - element.clientHeight < 160
    )
      void this.loadMore();
  }
  topup(row: CustomerAccountReportRow) {
    this.dialog.open(CustomerAccountOperationComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false,
      data: {
        clientId: row.client_id,
        clientName: row.client_name,
        orgId: row.org_id,
        orgName: this.organizations.find((org) => org.id === row.org_id)?.name,
        kind: 'TOPUP',
        summary: row
      }
    });
  }
}
